import { useChartColors } from '../lib/accessibility'
import { apiError, errStatus } from '../lib/errors'
/**
 * ReportsPage — yield analytics, year-over-year, weather correlation, expense vs revenue.
 *
 * Tabs:
 *   Yield Over Time   — line chart, date-range picker, granularity selector
 *   Year-over-Year    — grouped bar chart (current vs prior year by month)
 *   By Variety        — horizontal bar chart of kg per variety
 *   Weather vs Yield  — dual-axis line chart with Pearson correlation badge
 *   Expense Summary   — stacked area: expenses vs revenue
 *
 * Respects 402 analytics gate — shows upgrade prompt for Seedling tenants.
 */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, Cell,
} from 'recharts'
import {
  reportsApi, scheduledReportsApi, aiInsightsApi,
  type YoYMonth, type ExpenseRevenuePoint, type RevenueReport,
  type ScheduledReport, type ReportRun, type YieldAnalysis, type WeatherAnalysis, type PlantingRecommendation,
} from '../lib/api'

// ── Design tokens ──────────────────────────────────────────────────────────
const C = {
  bg:        'var(--color-bg)',
  card:      'var(--color-bg-card)',
  card2:     'var(--color-bg-card-2)',
  border:    'var(--color-border)',
  amber:     'var(--color-primary)',
  green:     '#4CAF50',
  greenDark: '#2D6A2F',
  red:       '#F87171',
  blue:      '#60A5FA',
  purple:    '#A78BFA',
  teal:      '#2DD4BF',
  text:      'var(--color-text)',
  textMuted: 'var(--color-text-muted)',
  textFaint: 'var(--color-text-faint)',
}

type Tab = 'yield' | 'yoy' | 'variety' | 'crop' | 'weather' | 'expense' | 'revenue' | 'insights' | 'scheduled'

// ── Shared UI ───────────────────────────────────────────────────────────────
function Card({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, ...style }}>
      {children}
    </div>
  )
}

function StatCard({ label, value, sub, accent }: { label: string; value: string | number; sub?: string; accent?: string }) {
  return (
    <Card style={{ padding: '16px 20px' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.textFaint, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: accent ?? C.text, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>{sub}</div>}
    </Card>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 11, fontWeight: 700, color: C.textFaint, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 14 }}>{children}</div>
}

function AnalyticsGate() {
  return (
    <Card style={{ textAlign: 'center', padding: '56px 32px' }}>
      <div style={{ fontSize: 40, marginBottom: 14 }}>📊</div>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: C.text, margin: '0 0 10px', fontFamily: '"Syne", system-ui' }}>Analytics requires Grower or Estate</h2>
      <p style={{ fontSize: 14, color: C.textMuted, margin: '0 0 24px', lineHeight: 1.6, maxWidth: 380, marginLeft: 'auto', marginRight: 'auto' }}>
        Unlock yield-over-time charts, year-over-year comparisons, and weather correlation reports by upgrading your plan.
      </p>
      <a href="/settings" style={{ display: 'inline-block', padding: '10px 24px', borderRadius: 9, background: C.amber, color: 'var(--color-bg)', fontWeight: 700, fontSize: 14, textDecoration: 'none' }}>
        Upgrade Plan →
      </a>
    </Card>
  )
}

const tooltipStyle = {
  background: C.card2, border: `1px solid ${C.border}`, borderRadius: 8,
  fontSize: 12, color: C.text, padding: '8px 12px',
}

// ── Tab: Yield Over Time ─────────────────────────────────────────────────────
function YieldTab() {
  const chartC = useChartColors()
  const [from, setFrom] = useState(() => {
    const d = new Date(); d.setFullYear(d.getFullYear() - 1); return d.toISOString().slice(0, 10)
  })
  const [to, setTo]           = useState(() => new Date().toISOString().slice(0, 10))
  const [granularity, setGran] = useState('month')

  const { data, isLoading, error } = useQuery({
    queryKey: ['reports-yield', from, to, granularity],
    queryFn: () => reportsApi.yieldOverTime({ from, to, granularity }),
  })

  const is402 = errStatus(error) === 402
  if (is402) return <AnalyticsGate />

  const series = data?.data?.series ?? []
  const summary = data?.data?.summary

  return (
    <div>
      {/* Controls */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>From</div>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)}
            style={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 13, padding: '7px 10px', outline: 'none' }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>To</div>
          <input type="date" value={to} onChange={e => setTo(e.target.value)}
            style={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 13, padding: '7px 10px', outline: 'none' }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>Granularity</div>
          <select value={granularity} onChange={e => setGran(e.target.value)}
            style={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 13, padding: '7px 10px', outline: 'none', cursor: 'pointer' }}>
            {['day', 'week', 'month', 'season', 'year'].map(g => <option key={g} value={g}>{g.charAt(0).toUpperCase() + g.slice(1)}</option>)}
          </select>
        </div>
      </div>

      {/* Summary stats */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginBottom: 24 }}>
          <StatCard label="Total yield" value={`${summary.total.toFixed(1)} kg`} />
          <StatCard label="Avg per period" value={`${summary.avg.toFixed(1)} kg`} />
          <StatCard label="Best period" value={`${summary.max.toFixed(1)} kg`} accent={C.green} />
          {summary.change_pct !== undefined && (
            <StatCard
              label="vs prior period"
              value={`${summary.change_pct > 0 ? '+' : ''}${summary.change_pct.toFixed(1)}%`}
              accent={summary.change_pct >= 0 ? C.green : C.red}
            />
          )}
        </div>
      )}

      {/* Chart */}
      <Card>
        <SectionLabel>Yield (kg) over time</SectionLabel>
        {isLoading ? (
          <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted }}>Loading…</div>
        ) : series.length === 0 ? (
          <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted, flexDirection: 'column', gap: 8 }}>
            <span style={{ fontSize: 28 }}>🌿</span>
            <span>No harvest records in this date range</span>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={series} margin={{ top: 5, right: 16, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartC.grid} />
              <XAxis dataKey="date" tick={{ fill: chartC.faint, fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fill: chartC.faint, fontSize: 11 }} tickLine={false} axisLine={false} unit=" kg" />
              <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [`${v.toFixed(1)} kg`, 'Yield']} />
              <Line type="monotone" dataKey="value" stroke={chartC.accent} strokeWidth={2.5} dot={false} activeDot={{ r: 5, fill: chartC.accent }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Card>
    </div>
  )
}

// ── Tab: Year-over-Year ─────────────────────────────────────────────────────
function YoYTab() {
  const chartC = useChartColors()
  const currentYear = new Date().getFullYear()
  const [compareYear, setCompareYear] = useState(currentYear - 1)

  const { data, isLoading, error } = useQuery({
    queryKey: ['reports-yoy', compareYear],
    queryFn: () => reportsApi.yieldYoY({ compare_year: compareYear }),
  })

  const is402 = errStatus(error) === 402
  if (is402) return <AnalyticsGate />

  const months: YoYMonth[] = data?.data?.months ?? []

  return (
    <div>
      {/* Controls */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>Compare against</div>
          <select value={compareYear} onChange={e => setCompareYear(Number(e.target.value))}
            style={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 13, padding: '7px 10px', outline: 'none', cursor: 'pointer' }}>
            {Array.from({ length: 5 }, (_, i) => currentYear - 1 - i).map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: C.textMuted }}>
          <span style={{ width: 14, height: 3, background: C.amber, display: 'inline-block', borderRadius: 2 }} />
          {currentYear}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: C.textMuted }}>
          <span style={{ width: 14, height: 3, background: C.blue, display: 'inline-block', borderRadius: 2, opacity: 0.7 }} />
          {compareYear}
        </div>
      </div>

      <Card>
        <SectionLabel>Yield by month — {currentYear} vs {compareYear}</SectionLabel>
        {isLoading ? (
          <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted }}>Loading…</div>
        ) : months.length === 0 ? (
          <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted }}>No data for this period</div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={months} margin={{ top: 5, right: 16, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartC.grid} vertical={false} />
              <XAxis dataKey="month_name" tick={{ fill: chartC.faint, fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fill: chartC.faint, fontSize: 11 }} tickLine={false} axisLine={false} unit=" kg" />
              <Tooltip contentStyle={tooltipStyle} formatter={(v: number, name: string) => [`${v.toFixed(1)} kg`, name === 'current_kg' ? currentYear : compareYear]} />
              <Bar dataKey="current_kg" name={String(currentYear)} fill={chartC.accent} radius={[3, 3, 0, 0]} />
              <Bar dataKey="prior_kg" name={String(compareYear)} fill={C.blue} opacity={0.7} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* Month detail table */}
      {months.length > 0 && (
        <Card style={{ marginTop: 14, padding: 0, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', padding: '10px 16px', borderBottom: `1px solid ${C.border}` }}>
            {['Month', currentYear, compareYear, 'Change'].map(h => (
              <div key={h} style={{ fontSize: 11, fontWeight: 700, color: C.textFaint, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</div>
            ))}
          </div>
          {months.filter(m => m.current_kg > 0 || m.prior_kg > 0).map(m => (
            <div key={m.month} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', padding: '9px 16px', borderBottom: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 13, color: C.text }}>{m.month_name}</div>
              <div style={{ fontSize: 13, color: C.text, fontWeight: m.current_kg > m.prior_kg ? 600 : 400 }}>{m.current_kg.toFixed(1)} kg</div>
              <div style={{ fontSize: 13, color: C.textMuted }}>{m.prior_kg.toFixed(1)} kg</div>
              <div style={{ fontSize: 13, color: m.change_pct > 0 ? C.green : m.change_pct < 0 ? C.red : C.textMuted, fontWeight: 600 }}>
                {m.change_pct > 0 ? '+' : ''}{m.change_pct.toFixed(0)}%
              </div>
            </div>
          ))}
        </Card>
      )}
    </div>
  )
}

// ── Tab: By Variety ──────────────────────────────────────────────────────────
function CropTab() {
  const chartC = useChartColors()
  const [from, setFrom] = useState(() => {
    const d = new Date(); d.setFullYear(d.getFullYear() - 1); return d.toISOString().slice(0, 10)
  })
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10))

  const { data, isLoading, error } = useQuery({
    queryKey: ['reports-crop-per-acre', from, to],
    queryFn: () => reportsApi.cropPerAcre({ from, to }),
  })
  if (errStatus(error) === 402) return <AnalyticsGate />

  const series = (data?.data?.series ?? [])
  const withAcres = series.filter(s => s.acreage > 0)
  const sum = data?.data?.summary
  const COLORS = [chartC.accent, C.green, C.blue, C.purple, C.teal, '#FB923C', '#E879F9', '#34D399']

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        {[['From', from, setFrom], ['To', to, setTo]].map(([label, val, set]) => (
          <div key={label as string}>
            <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>{label as string}</div>
            <input type="date" value={val as string} onChange={e => (set as (v: string) => void)(e.target.value)}
              style={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 13, padding: '7px 10px', outline: 'none' }} />
          </div>
        ))}
      </div>

      {isLoading ? (
        <Card style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted }}>Loading…</Card>
      ) : series.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: '48px 24px' }}>
          <span style={{ fontSize: 32 }}>🌾</span>
          <p style={{ color: C.textMuted, marginTop: 12 }}>No blocks yet</p>
          <p style={{ color: C.textFaint, fontSize: 12 }}>Create blocks with an acreage on the Blocks page to see yield per acre.</p>
        </Card>
      ) : (
        <>
          {sum && (
            <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
              {[
                ['Total yield', `${sum.total_kg.toFixed(0)} kg`],
                ['Total acreage', `${sum.total_acres.toFixed(1)} ac`],
                ['Avg yield/acre', `${sum.avg_yield_per_acre.toFixed(0)} kg/ac`],
                ['Total revenue', `$${sum.total_revenue.toFixed(0)}`],
              ].map(([l, v]) => (
                <Card key={l} style={{ flex: '1 1 140px' }}>
                  <div style={{ fontSize: 11, color: C.textMuted }}>{l}</div>
                  <div style={{ fontSize: 20, fontWeight: 700, marginTop: 2 }}>{v}</div>
                </Card>
              ))}
            </div>
          )}

          {withAcres.length > 0 && (
            <Card style={{ marginBottom: 14 }}>
              <SectionLabel>Yield per acre (kg/ac)</SectionLabel>
              <ResponsiveContainer width="100%" height={Math.max(200, withAcres.length * 44)}>
                <BarChart layout="vertical" data={[...withAcres].sort((a, b) => b.yield_per_acre - a.yield_per_acre)} margin={{ top: 0, right: 60, bottom: 0, left: 80 }}>
                  <XAxis type="number" tick={{ fill: chartC.faint, fontSize: 11 }} axisLine={false} tickLine={false} unit=" kg/ac" />
                  <YAxis type="category" dataKey="block_name" tick={{ fill: chartC.muted, fontSize: 12 }} axisLine={false} tickLine={false} width={76} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [`${v.toFixed(0)} kg/ac`, 'Yield/acre']} />
                  <Bar dataKey="yield_per_acre" radius={[0, 4, 4, 0]}>
                    {withAcres.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}

          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto auto auto', padding: '10px 16px', borderBottom: `1px solid ${C.border}`, gap: 16 }}>
              {['Block', 'Acres', 'Yield (kg)', 'kg/acre', 'Rev/acre', 'Loss'].map(h => (
                <div key={h} style={{ fontSize: 11, fontWeight: 700, color: C.textFaint, textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: h !== 'Block' ? 'right' : 'left' }}>{h}</div>
              ))}
            </div>
            {series.map(s => (
              <div key={s.block_name} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto auto auto', padding: '10px 16px', borderBottom: `1px solid ${C.border}`, gap: 16, alignItems: 'center', fontSize: 13 }}>
                <div style={{ fontWeight: 600 }}>{s.block_name}</div>
                <div style={{ textAlign: 'right', color: C.textMuted }}>{s.acreage > 0 ? s.acreage.toFixed(2) : '—'}</div>
                <div style={{ textAlign: 'right' }}>{s.total_kg.toFixed(0)}</div>
                <div style={{ textAlign: 'right', fontWeight: 700 }}>{s.acreage > 0 ? s.yield_per_acre.toFixed(0) : '—'}</div>
                <div style={{ textAlign: 'right', color: C.green }}>{s.acreage > 0 && s.revenue_per_acre > 0 ? `$${s.revenue_per_acre.toFixed(0)}` : '—'}</div>
                <div style={{ textAlign: 'right', color: s.loss_rate_pct > 0 ? '#dc2626' : C.textFaint }} title={s.lost_kg > 0 ? `${s.lost_kg.toFixed(0)} kg lost` : 'no loss logged'}>{s.lost_kg > 0 ? `${s.loss_rate_pct.toFixed(0)}%` : '—'}</div>
              </div>
            ))}
          </Card>
        </>
      )}
    </div>
  )
}

function VarietyTab() {
  const chartC = useChartColors()
  const [from, setFrom] = useState(() => {
    const d = new Date(); d.setFullYear(d.getFullYear() - 1); return d.toISOString().slice(0, 10)
  })
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10))

  const { data, isLoading, error } = useQuery({
    queryKey: ['reports-variety', from, to],
    queryFn: () => reportsApi.yieldByVariety({ from, to }),
  })

  const is402 = errStatus(error) === 402
  if (is402) return <AnalyticsGate />

  const series = (data?.data?.series ?? []).sort((a, b) => b.total_kg - a.total_kg)
  const maxKg = series[0]?.total_kg ?? 1

  const COLORS = [chartC.accent, C.green, C.blue, C.purple, C.teal, '#FB923C', '#E879F9', '#34D399']

  return (
    <div>
      {/* Date range */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        {[['From', from, setFrom], ['To', to, setTo]].map(([label, val, set]) => (
          <div key={label as string}>
            <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>{label as string}</div>
            <input type="date" value={val as string} onChange={e => (set as (v: string) => void)(e.target.value)}
              style={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 13, padding: '7px 10px', outline: 'none' }} />
          </div>
        ))}
      </div>

      {isLoading ? (
        <Card style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted }}>Loading…</Card>
      ) : series.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: '48px 24px' }}>
          <span style={{ fontSize: 32 }}>🌾</span>
          <p style={{ color: C.textMuted, marginTop: 12 }}>No harvest data in this range</p>
        </Card>
      ) : (
        <>
          <Card style={{ marginBottom: 14 }}>
            <SectionLabel>Yield by variety (kg)</SectionLabel>
            <ResponsiveContainer width="100%" height={Math.max(200, series.length * 44)}>
              <BarChart layout="vertical" data={series} margin={{ top: 0, right: 60, bottom: 0, left: 80 }}>
                <XAxis type="number" tick={{ fill: chartC.faint, fontSize: 11 }} axisLine={false} tickLine={false} unit=" kg" />
                <YAxis type="category" dataKey="variety_name" tick={{ fill: chartC.muted, fontSize: 12 }} axisLine={false} tickLine={false} width={76} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [`${v.toFixed(1)} kg`, 'Yield']} />
                <Bar dataKey="total_kg" radius={[0, 4, 4, 0]}>
                  {series.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>

          {/* Table */}
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', padding: '10px 16px', borderBottom: `1px solid ${C.border}`, gap: 16 }}>
              {['Variety', 'Yield (kg)', 'Revenue', '% of total'].map(h => (
                <div key={h} style={{ fontSize: 11, fontWeight: 700, color: C.textFaint, textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: h !== 'Variety' ? 'right' : 'left' }}>{h}</div>
              ))}
            </div>
            {series.map((s, i) => (
              <div key={s.variety_name} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', padding: '10px 16px', borderBottom: `1px solid ${C.border}`, gap: 16, alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: COLORS[i % COLORS.length], flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: C.text, fontWeight: 500 }}>{s.variety_name}</span>
                </div>
                <div style={{ fontSize: 13, color: C.text, textAlign: 'right', fontWeight: 600 }}>{s.total_kg.toFixed(1)}</div>
                <div style={{ fontSize: 13, color: C.textMuted, textAlign: 'right' }}>{s.total_revenue > 0 ? `$${s.total_revenue.toFixed(0)}` : '—'}</div>
                <div style={{ fontSize: 13, color: C.textMuted, textAlign: 'right' }}>{((s.total_kg / maxKg) * 100).toFixed(0)}%</div>
              </div>
            ))}
          </Card>
        </>
      )}
    </div>
  )
}

// ── Tab: Weather vs Yield ────────────────────────────────────────────────────
function WeatherTab() {
  const chartC = useChartColors()
  const [metric, setMetric] = useState('temp')
  const [from, setFrom] = useState(() => {
    const d = new Date(); d.setFullYear(d.getFullYear() - 1); return d.toISOString().slice(0, 10)
  })
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10))

  const { data, isLoading, error } = useQuery({
    queryKey: ['reports-weather', from, to, metric],
    queryFn: () => reportsApi.yieldVsWeather({ from, to, metric }),
  })

  const is402 = errStatus(error) === 402
  if (is402) return <AnalyticsGate />

  const yieldSeries = data?.data?.yield_series ?? []
  const weatherSeries = data?.data?.weather_series ?? []
  const correlation = data?.data?.correlation

  // Merge into one series for dual-axis chart
  const merged = yieldSeries.map((pt, i) => ({
    date: pt.date,
    yield: pt.value,
    weather: weatherSeries[i]?.value ?? null,
  }))

  const metricLabel: Record<string, string> = {
    temp: 'Avg Temperature (°C)',
    rainfall: 'Rainfall (mm)',
    et: 'ET (mm)',
    solar: 'Solar Rad (W/m²)',
    humidity: 'Humidity (%)',
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>Weather metric</div>
          <select value={metric} onChange={e => setMetric(e.target.value)}
            style={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 13, padding: '7px 10px', outline: 'none', cursor: 'pointer' }}>
            {Object.entries(metricLabel).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        {[['From', from, setFrom], ['To', to, setTo]].map(([label, val, set]) => (
          <div key={label as string}>
            <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>{label as string}</div>
            <input type="date" value={val as string} onChange={e => (set as (v: string) => void)(e.target.value)}
              style={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 13, padding: '7px 10px', outline: 'none' }} />
          </div>
        ))}
      </div>

      {correlation !== undefined && (
        <div style={{ marginBottom: 20, display: 'flex', gap: 10, alignItems: 'center' }}>
          <div style={{ padding: '8px 16px', borderRadius: 8, background: C.card2, border: `1px solid ${C.border}` }}>
            <span style={{ fontSize: 12, color: C.textMuted }}>Pearson correlation: </span>
            <span style={{
              fontSize: 15, fontWeight: 700,
              color: Math.abs(correlation) > 0.7 ? C.green : Math.abs(correlation) > 0.4 ? C.amber : C.red,
            }}>
              {correlation.toFixed(2)}
            </span>
            <span style={{ fontSize: 11, color: C.textFaint, marginLeft: 8 }}>
              {Math.abs(correlation) > 0.7 ? '(strong)' : Math.abs(correlation) > 0.4 ? '(moderate)' : '(weak)'}
            </span>
          </div>
        </div>
      )}

      <Card>
        <SectionLabel>Yield vs {metricLabel[metric]}</SectionLabel>
        {isLoading ? (
          <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted }}>Loading…</div>
        ) : merged.length === 0 ? (
          <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted }}>No data available</div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={merged} margin={{ top: 5, right: 32, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartC.grid} />
              <XAxis dataKey="date" tick={{ fill: chartC.faint, fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis yAxisId="yield" tick={{ fill: chartC.faint, fontSize: 11 }} tickLine={false} axisLine={false} unit=" kg" orientation="left" />
              <YAxis yAxisId="weather" tick={{ fill: chartC.faint, fontSize: 11 }} tickLine={false} axisLine={false} orientation="right" />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 12, color: C.textMuted }} />
              <Line yAxisId="yield" type="monotone" dataKey="yield" name="Yield (kg)" stroke={chartC.accent} strokeWidth={2.5} dot={false} />
              <Line yAxisId="weather" type="monotone" dataKey="weather" name={metricLabel[metric]} stroke={C.blue} strokeWidth={1.5} dot={false} strokeDasharray="4 2" />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Card>
    </div>
  )
}

// ── Tab: Expense vs Revenue ──────────────────────────────────────────────────
function ExpenseTab() {
  const chartC = useChartColors()
  const [from, setFrom] = useState(() => {
    const d = new Date(); d.setFullYear(d.getFullYear() - 1); return d.toISOString().slice(0, 10)
  })
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10))

  const { data, isLoading, error } = useQuery({
    queryKey: ['reports-expense', from, to],
    queryFn: () => reportsApi.expenseVsRevenue({ from, to }),
  })

  const is402 = errStatus(error) === 402
  if (is402) return <AnalyticsGate />

  const series: ExpenseRevenuePoint[] = data?.data?.series ?? []
  const totalExpenses = series.reduce((s, p) => s + p.expenses, 0)
  const totalRevenue  = series.reduce((s, p) => s + p.revenue, 0)
  const netIncome     = totalRevenue - totalExpenses

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        {[['From', from, setFrom], ['To', to, setTo]].map(([label, val, set]) => (
          <div key={label as string}>
            <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>{label as string}</div>
            <input type="date" value={val as string} onChange={e => (set as (v: string) => void)(e.target.value)}
              style={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 13, padding: '7px 10px', outline: 'none' }} />
          </div>
        ))}
      </div>

      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: 24 }}>
        <StatCard label="Total revenue" value={`$${totalRevenue.toFixed(0)}`} accent={C.green} />
        <StatCard label="Total expenses" value={`$${totalExpenses.toFixed(0)}`} accent={C.red} />
        <StatCard label="Net income" value={`${netIncome >= 0 ? '+' : ''}$${netIncome.toFixed(0)}`} accent={netIncome >= 0 ? C.green : C.red} />
        {totalRevenue > 0 && <StatCard label="Margin" value={`${((netIncome / totalRevenue) * 100).toFixed(0)}%`} />}
      </div>

      <Card>
        <SectionLabel>Revenue vs Expenses</SectionLabel>
        {isLoading ? (
          <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted }}>Loading…</div>
        ) : series.length === 0 ? (
          <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted, flexDirection: 'column', gap: 8 }}>
            <span style={{ fontSize: 28 }}>💰</span>
            <span>No expense or revenue data in this range</span>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={series} margin={{ top: 5, right: 16, bottom: 5, left: 0 }}>
              <defs>
                <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={C.green} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={C.green} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="expGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={C.red} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={C.red} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={chartC.grid} />
              <XAxis dataKey="month" tick={{ fill: chartC.faint, fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fill: chartC.faint, fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={v => `$${v}`} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v: number, name: string) => [`$${v.toFixed(0)}`, name]} />
              <ReferenceLine y={0} stroke={chartC.grid} />
              <Area type="monotone" dataKey="revenue" name="Revenue" stroke={C.green} fill="url(#revGrad)" strokeWidth={2} />
              <Area type="monotone" dataKey="expenses" name="Expenses" stroke={C.red} fill="url(#expGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Card>
    </div>
  )
}

// ── Main page ────────────────────────────────────────────────────────────────
// ── Revenue by Channel ────────────────────────────────────────────────────────
const CHANNEL_LABELS: Record<string, string> = {
  wholesale: 'Wholesale', farmstand: 'Farm stand', online: 'Online',
  facebook: 'Facebook', farmers_market: 'Farmers market', other: 'Other',
}
const channelLabel = (c: string) => CHANNEL_LABELS[c] ?? c
const money = (cents: number, dp = 0) => `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp })}`

function RevenueTab() {
  const chartC = useChartColors()
  const [from, setFrom] = useState(() => {
    const d = new Date(); d.setFullYear(d.getFullYear() - 1); return d.toISOString().slice(0, 10)
  })
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10))

  const { data, isLoading, error } = useQuery({
    queryKey: ['reports-revenue', from, to],
    queryFn: () => reportsApi.revenueByChannel({ from, to }),
  })
  if (errStatus(error) === 402) return <AnalyticsGate />

  const rep: RevenueReport | undefined = data?.data
  const chanData = (rep?.by_channel ?? []).map(c => ({
    label: channelLabel(c.channel), revenue: c.revenue_cents / 100, kg: c.quantity_kg, count: c.sale_count,
    isWholesale: c.channel === 'wholesale',
  }))
  const hasData = (rep?.by_channel?.length ?? 0) > 0

  return (
    <div>
      {/* Controls */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>From</div>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)}
            style={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 13, padding: '7px 10px', outline: 'none' }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 4 }}>To</div>
          <input type="date" value={to} onChange={e => setTo(e.target.value)}
            style={{ background: C.card2, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 13, padding: '7px 10px', outline: 'none' }} />
        </div>
      </div>

      {/* Summary stats */}
      {rep && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: 24 }}>
          <StatCard label="Total revenue" value={money(rep.total_revenue_cents)} accent={C.green} />
          <StatCard label="Retail" value={money(rep.retail_revenue_cents)}
            sub={rep.total_revenue_cents > 0 ? `${Math.round((rep.retail_revenue_cents / rep.total_revenue_cents) * 100)}%` : undefined} />
          <StatCard label="Wholesale" value={money(rep.wholesale_revenue_cents)}
            sub={rep.total_revenue_cents > 0 ? `${Math.round((rep.wholesale_revenue_cents / rep.total_revenue_cents) * 100)}%` : undefined} />
          <StatCard label="Volume sold" value={`${rep.total_kg.toFixed(0)} kg`} />
        </div>
      )}

      {/* Revenue by channel */}
      <Card>
        <SectionLabel>Revenue by channel</SectionLabel>
        {isLoading ? (
          <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted }}>Loading…</div>
        ) : !hasData ? (
          <div style={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted, flexDirection: 'column', gap: 8 }}>
            <span style={{ fontSize: 28 }}>🏷</span>
            <span>No sales recorded in this date range</span>
            <span style={{ fontSize: 12, color: C.textFaint }}>Allocate harvests to retail/wholesale on the Harvest page.</span>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chanData} margin={{ top: 5, right: 16, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartC.grid} vertical={false} />
              <XAxis dataKey="label" tick={{ fill: chartC.faint, fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fill: chartC.faint, fontSize: 11 }} tickLine={false} axisLine={false} unit="$" />
              <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => [`$${v.toFixed(2)}`, 'Revenue']} />
              <Bar dataKey="revenue" radius={[3, 3, 0, 0]}>
                {chanData.map((d, i) => <Cell key={i} fill={d.isWholesale ? chartC.accent2 : chartC.accent} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* Wholesale partners */}
      {(rep?.by_partner?.length ?? 0) > 0 && (
        <Card style={{ marginTop: 14, padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px 10px' }}><SectionLabel>Wholesale partners</SectionLabel></div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ color: C.textFaint, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                <th style={{ textAlign: 'left', padding: '6px 20px' }}>Partner</th>
                <th style={{ textAlign: 'right', padding: '6px 20px' }}>Revenue</th>
                <th style={{ textAlign: 'right', padding: '6px 20px' }}>Volume</th>
                <th style={{ textAlign: 'right', padding: '6px 20px' }}>Sales</th>
              </tr>
            </thead>
            <tbody>
              {rep!.by_partner.map(p => (
                <tr key={p.vendor_id} style={{ borderTop: `1px solid ${C.border}` }}>
                  <td style={{ padding: '9px 20px', color: C.text, fontWeight: 600 }}>{p.vendor_name}</td>
                  <td style={{ padding: '9px 20px', textAlign: 'right', color: C.green, fontWeight: 600 }}>{money(p.revenue_cents, 2)}</td>
                  <td style={{ padding: '9px 20px', textAlign: 'right', color: C.textMuted }}>{p.quantity_kg.toFixed(0)} kg</td>
                  <td style={{ padding: '9px 20px', textAlign: 'right', color: C.textMuted }}>{p.sale_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  )
}

// ── Tab: Scheduled Reports ───────────────────────────────────────────────────
// ── Tab: Insights (AI yield + weather analysis) ─────────────────────────────
function InsightCard({ title, source, children, onGenerate, loading, generated }: {
  title: string; source?: 'vertex' | 'fallback'; children?: React.ReactNode
  onGenerate: () => void; loading: boolean; generated: boolean
}) {
  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: C.text, fontFamily: '"Syne", system-ui' }}>{title}</span>
          {generated && source && (
            <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: 'color-mix(in srgb, var(--color-primary) 16%, transparent)', color: C.amber }}>
              {source === 'vertex' ? 'AUTO' : 'SUMMARY'}
            </span>
          )}
        </div>
        <button onClick={onGenerate} disabled={loading} style={{
          padding: '7px 14px', borderRadius: 8, border: 'none', cursor: loading ? 'default' : 'pointer',
          background: C.amber, color: 'var(--color-bg)', fontWeight: 700, fontSize: 12.5, opacity: loading ? 0.6 : 1,
        }}>{loading ? 'Analyzing…' : generated ? 'Regenerate' : 'Generate'}</button>
      </div>
      {children}
    </Card>
  )
}

function InsightsTab() {
  const yieldM = useMutation({ mutationFn: () => aiInsightsApi.analyzeYield().then(r => r.data) })
  const weatherM = useMutation({ mutationFn: () => aiInsightsApi.analyzeWeather().then(r => r.data) })
  const plantingM = useMutation({ mutationFn: () => aiInsightsApi.recommendPlanting().then(r => r.data) })
  const y: YieldAnalysis | undefined = yieldM.data
  const w: WeatherAnalysis | undefined = weatherM.data
  const pl: PlantingRecommendation | undefined = plantingM.data

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <p style={{ fontSize: 13, color: C.textMuted, margin: 0, lineHeight: 1.6 }}>
        Plain-language analysis of your harvest and weather history. Generated automatically when your
        plan includes AI; otherwise a rule-based summary of the same numbers.
      </p>

      <InsightCard title="Yield analysis" source={y?.source} onGenerate={() => yieldM.mutate()} loading={yieldM.isPending} generated={!!y}>
        {yieldM.error && <p style={{ fontSize: 13, color: C.red }}>{apiError(yieldM.error, 'Could not analyze yield')}</p>}
        {!y && !yieldM.isPending && !yieldM.error && <p style={{ fontSize: 13, color: C.textFaint, margin: 0 }}>Click Generate for a summary of yield over the last 24 months, top varieties, and any weather correlation.</p>}
        {y && (
          <div>
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 12 }}>
              <div><div style={{ fontSize: 11, color: C.textFaint, fontWeight: 700, textTransform: 'uppercase' }}>Total</div><div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{y.total_kg.toLocaleString()} kg</div></div>
              <div><div style={{ fontSize: 11, color: C.textFaint, fontWeight: 700, textTransform: 'uppercase' }}>Months</div><div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{y.months}</div></div>
              {y.top_varieties[0] && <div><div style={{ fontSize: 11, color: C.textFaint, fontWeight: 700, textTransform: 'uppercase' }}>Top variety</div><div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{y.top_varieties[0].name}</div></div>}
              {y.weather_correlation != null && <div><div style={{ fontSize: 11, color: C.textFaint, fontWeight: 700, textTransform: 'uppercase' }}>Temp corr.</div><div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{y.weather_correlation > 0 ? '+' : ''}{y.weather_correlation.toFixed(2)}</div></div>}
            </div>
            <p style={{ fontSize: 13.5, lineHeight: 1.6, color: C.text, whiteSpace: 'pre-wrap', margin: 0 }}>{y.narrative}</p>
            <p style={{ fontSize: 11, color: C.textFaint, margin: '10px 0 0' }}>{y.disclaimer}</p>
          </div>
        )}
      </InsightCard>

      <InsightCard title="Weather analysis" source={w?.source} onGenerate={() => weatherM.mutate()} loading={weatherM.isPending} generated={!!w}>
        {weatherM.error && <p style={{ fontSize: 13, color: C.red }}>{apiError(weatherM.error, 'Could not analyze weather')}</p>}
        {!w && !weatherM.isPending && !weatherM.error && <p style={{ fontSize: 13, color: C.textFaint, margin: 0 }}>Click Generate for temperature trends, rainfall totals, and frost-risk windows from your weather station history.</p>}
        {w && (
          <div>
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 12 }}>
              <div><div style={{ fontSize: 11, color: C.textFaint, fontWeight: 700, textTransform: 'uppercase' }}>Months</div><div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{w.months}</div></div>
              {w.avg_temp_c != null && <div><div style={{ fontSize: 11, color: C.textFaint, fontWeight: 700, textTransform: 'uppercase' }}>Avg temp</div><div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{w.avg_temp_c.toFixed(1)}°C</div></div>}
              {w.total_rainfall_mm != null && <div><div style={{ fontSize: 11, color: C.textFaint, fontWeight: 700, textTransform: 'uppercase' }}>Rainfall</div><div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{Math.round(w.total_rainfall_mm)} mm</div></div>}
              <div><div style={{ fontSize: 11, color: C.textFaint, fontWeight: 700, textTransform: 'uppercase' }}>Frost months</div><div style={{ fontSize: 20, fontWeight: 700, color: w.frost_months > 0 ? C.blue : C.text }}>{w.frost_months}</div></div>
            </div>
            <p style={{ fontSize: 13.5, lineHeight: 1.6, color: C.text, whiteSpace: 'pre-wrap', margin: 0 }}>{w.narrative}</p>
            <p style={{ fontSize: 11, color: C.textFaint, margin: '10px 0 0' }}>{w.disclaimer}</p>
          </div>
        )}
      </InsightCard>

      <InsightCard title="Planting recommendations" source={pl?.source} onGenerate={() => plantingM.mutate()} loading={plantingM.isPending} generated={!!pl}>
        {plantingM.error && <p style={{ fontSize: 13, color: C.red }}>{apiError(plantingM.error, 'Could not generate recommendations')}</p>}
        {!pl && !plantingM.isPending && !plantingM.error && <p style={{ fontSize: 13, color: C.textFaint, margin: 0 }}>Click Generate for varieties to plant next that complement your farm — spreading the harvest window, aiding pollination, and fitting your region.</p>}
        {pl && (
          <div>
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 12 }}>
              {pl.region && <div><div style={{ fontSize: 11, color: C.textFaint, fontWeight: 700, textTransform: 'uppercase' }}>Region</div><div style={{ fontSize: 18, fontWeight: 700, color: C.text }}>{pl.region}</div></div>}
              {pl.current_categories.slice(0, 4).map(c => (
                <div key={c.category}><div style={{ fontSize: 11, color: C.textFaint, fontWeight: 700, textTransform: 'uppercase' }}>{c.category}</div><div style={{ fontSize: 18, fontWeight: 700, color: C.text }}>{c.count}</div></div>
              ))}
            </div>
            <p style={{ fontSize: 13.5, lineHeight: 1.6, color: C.text, whiteSpace: 'pre-wrap', margin: 0 }}>{pl.narrative}</p>
            <p style={{ fontSize: 11, color: C.textFaint, margin: '10px 0 0' }}>{pl.disclaimer}</p>
          </div>
        )}
      </InsightCard>
    </div>
  )
}

const REPORT_TYPES: { id: string; label: string }[] = [
  { id: 'yield_over_time',    label: 'Yield over time' },
  { id: 'yield_by_variety',   label: 'Yield by variety' },
  { id: 'year_over_year',     label: 'Year over year' },
  { id: 'yield_vs_weather',   label: 'Yield vs weather' },
  { id: 'care_summary',       label: 'Care summary' },
  { id: 'expense_vs_revenue', label: 'Expenses vs revenue' },
  { id: 'inventory_usage',    label: 'Inventory usage' },
]
const typeLabel = (id: string) => REPORT_TYPES.find(t => t.id === id)?.label ?? id
const SCHEDULES = ['daily', 'weekly', 'monthly']

const runStatusColor: Record<string, string> = { success: C.green, failed: C.red, running: C.amber }

const inp: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', background: C.card2,
  border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 12px',
  color: C.text, fontSize: 13, outline: 'none',
}
const fieldLabel: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 600, color: C.textMuted,
  marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em',
}

function ScheduleModal({ report, onClose }: { report: ScheduledReport | null; onClose: () => void }) {
  const qc = useQueryClient()
  const isEdit = !!report
  const [form, setForm] = useState({
    name: report?.name ?? '',
    report_type: report?.report_type ?? 'yield_over_time',
    schedule: report?.schedule ?? 'weekly',
    destination: report?.destination ?? 'email',
    email_to: (report?.email_to ?? []).join(', '),
    days: report?.filters?.days?.toString() ?? '',
  })
  const [error, setError] = useState<string | null>(null)
  const set = <K extends keyof typeof form>(k: K, v: string) => setForm(f => ({ ...f, [k]: v }))

  const save = useMutation({
    mutationFn: () => {
      const body: Partial<ScheduledReport> = {
        name: form.name.trim(),
        report_type: form.report_type,
        schedule: form.schedule,
        format: 'csv',
        destination: form.destination,
        email_to: form.email_to.split(',').map(s => s.trim()).filter(Boolean),
        filters: form.days ? { days: parseInt(form.days) || undefined } : {},
        is_active: true,
      }
      return (isEdit ? scheduledReportsApi.update(report!.id, body) : scheduledReportsApi.create(body)) as Promise<unknown>
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['scheduled-reports'] }); onClose() },
    onError: (e) => setError(apiError(e, 'Failed to save')),
  })

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.name.trim()) { setError('Name is required'); return }
    if (form.destination === 'email' && !form.email_to.trim()) { setError('Add at least one recipient email'); return }
    save.mutate()
  }

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, width: 460, maxWidth: '95vw' }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}` }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>{isEdit ? 'Edit scheduled report' : 'New scheduled report'}</h2>
        </div>
        <form onSubmit={submit} style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div><label style={fieldLabel}>Name</label><input style={inp} value={form.name} onChange={e => set('name', e.target.value)} placeholder="Weekly yield summary" /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={fieldLabel}>Report</label>
              <select style={{ ...inp, cursor: 'pointer' }} value={form.report_type} onChange={e => set('report_type', e.target.value)}>
                {REPORT_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label style={fieldLabel}>Schedule</label>
              <select style={{ ...inp, cursor: 'pointer' }} value={form.schedule} onChange={e => set('schedule', e.target.value)}>
                {SCHEDULES.map(s => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={fieldLabel}>Delivery</label>
              <select style={{ ...inp, cursor: 'pointer' }} value={form.destination} onChange={e => set('destination', e.target.value)}>
                <option value="email">Email recipients</option>
                <option value="gcs">Store only (download here)</option>
              </select>
            </div>
            <div><label style={fieldLabel}>Window (days)</label><input style={inp} type="number" min={1} value={form.days} onChange={e => set('days', e.target.value)} placeholder="auto" /></div>
          </div>
          {form.destination === 'email' && (
            <div><label style={fieldLabel}>Email to (comma-separated)</label><input style={inp} value={form.email_to} onChange={e => set('email_to', e.target.value)} placeholder="you@farm.com, manager@farm.com" /></div>
          )}
          {error && <div style={{ fontSize: 12, color: C.red, padding: '8px 12px', background: 'rgba(248,113,113,0.1)', borderRadius: 8 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: '9px 0', borderRadius: 8, background: C.card2, border: `1px solid ${C.border}`, color: C.textMuted, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
            <button type="submit" disabled={save.isPending} style={{ flex: 2, padding: '9px 0', borderRadius: 8, background: C.amber, border: 'none', color: 'var(--color-bg)', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
              {save.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function RunHistory({ reportId }: { reportId: string }) {
  const { data, isLoading } = useQuery({ queryKey: ['report-runs', reportId], queryFn: () => scheduledReportsApi.runs(reportId).then(r => r.data.runs) })
  if (isLoading) return <div style={{ padding: '8px 16px', fontSize: 12, color: C.textFaint }}>Loading runs…</div>
  const runs = data ?? []
  if (runs.length === 0) return <div style={{ padding: '8px 16px', fontSize: 12, color: C.textFaint }}>No runs yet.</div>
  return (
    <div style={{ padding: '4px 16px 12px', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {runs.map((run: ReportRun) => (
        <div key={run.id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: C.textMuted, padding: '5px 0' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: runStatusColor[run.status] ?? C.textFaint, flexShrink: 0 }} />
          <span style={{ flex: 1 }}>{new Date(run.started_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
          <span style={{ color: runStatusColor[run.status] ?? C.textFaint }}>{run.status}</span>
          {run.status === 'success' && <span style={{ color: C.textFaint }}>{run.row_count} rows</span>}
          {run.status === 'failed' && run.error_message && <span title={run.error_message} style={{ color: C.red, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{run.error_message}</span>}
          {run.status === 'success' && (
            <button onClick={() => scheduledReportsApi.download(reportId, run.id)} style={{ padding: '3px 10px', borderRadius: 6, background: C.card2, border: `1px solid ${C.border}`, color: C.amber, cursor: 'pointer', fontSize: 11 }}>Download</button>
          )}
        </div>
      ))}
    </div>
  )
}

function ScheduledTab() {
  const qc = useQueryClient()
  const [modal, setModal] = useState<ScheduledReport | null | 'new'>(null)
  const [expanded, setExpanded] = useState<string | null>(null)
  const { data, isLoading, error } = useQuery({
    queryKey: ['scheduled-reports'],
    queryFn: () => scheduledReportsApi.list().then(r => r.data.reports),
  })
  const runNow = useMutation({
    mutationFn: (id: string) => scheduledReportsApi.run(id),
    onSuccess: (_d, id) => { qc.invalidateQueries({ queryKey: ['scheduled-reports'] }); qc.invalidateQueries({ queryKey: ['report-runs', id] }) },
  })
  const remove = useMutation({
    mutationFn: (id: string) => scheduledReportsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scheduled-reports'] }),
  })

  // 402 → analytics gate (export permission == grower/estate)
  if (errStatus(error) === 402 || errStatus(error) === 403) return <AnalyticsGate />

  const reports = data ?? []

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <SectionLabel>Recurring report exports — delivered by email or downloadable here</SectionLabel>
        <button onClick={() => setModal('new')} style={{ padding: '8px 16px', borderRadius: 9, background: C.amber, border: 'none', color: 'var(--color-bg)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>+ New report</button>
      </div>

      {isLoading ? <Card style={{ textAlign: 'center', color: C.textFaint }}>Loading…</Card> :
        reports.length === 0 ? (
          <Card style={{ textAlign: 'center', padding: '48px 24px' }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>🗓</div>
            <p style={{ fontSize: 14, color: C.textMuted, margin: '0 0 6px' }}>No scheduled reports yet.</p>
            <p style={{ fontSize: 12, color: C.textFaint, margin: '0 0 16px' }}>Schedule a recurring yield, expense, or care report — we'll generate and email it automatically.</p>
            <button onClick={() => setModal('new')} style={{ padding: '9px 18px', borderRadius: 8, background: C.amber, border: 'none', color: 'var(--color-bg)', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Schedule a report</button>
          </Card>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {reports.map(rep => (
              <Card key={rep.id} style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{rep.name}</div>
                    <div style={{ fontSize: 12, color: C.textFaint, marginTop: 2 }}>
                      {typeLabel(rep.report_type)} · {rep.schedule}
                      {rep.last_run_at && <> · last run {new Date(rep.last_run_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}{rep.last_run_status && <span style={{ color: runStatusColor[rep.last_run_status] ?? C.textFaint }}> ({rep.last_run_status})</span>}</>}
                      {rep.next_run_at && <> · next {new Date(rep.next_run_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}</>}
                    </div>
                  </div>
                  <button onClick={() => runNow.mutate(rep.id)} disabled={runNow.isPending} style={{ padding: '6px 12px', borderRadius: 7, background: C.amber, border: 'none', color: 'var(--color-bg)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                    {runNow.isPending && runNow.variables === rep.id ? 'Running…' : 'Run now'}
                  </button>
                  <button onClick={() => setExpanded(e => e === rep.id ? null : rep.id)} style={{ padding: '6px 12px', borderRadius: 7, background: C.card2, border: `1px solid ${C.border}`, color: C.textMuted, cursor: 'pointer', fontSize: 12 }}>History</button>
                  <button onClick={() => setModal(rep)} style={{ padding: '6px 12px', borderRadius: 7, background: C.card2, border: `1px solid ${C.border}`, color: C.textMuted, cursor: 'pointer', fontSize: 12 }}>Edit</button>
                  <button onClick={() => { if (confirm(`Delete "${rep.name}"?`)) remove.mutate(rep.id) }} style={{ padding: '6px 10px', borderRadius: 7, background: 'transparent', border: `1px solid ${C.red}`, color: C.red, cursor: 'pointer', fontSize: 12 }}>Delete</button>
                </div>
                {expanded === rep.id && <div style={{ borderTop: `1px solid ${C.border}` }}><RunHistory reportId={rep.id} /></div>}
              </Card>
            ))}
          </div>
        )}

      {modal !== null && <ScheduleModal report={modal === 'new' ? null : modal} onClose={() => setModal(null)} />}
    </div>
  )
}

export function ReportsPage() {
  const [tab, setTab] = useState<Tab>('yield')

  const tabs: { id: Tab; label: string; icon: string }[] = [
    { id: 'yield',   label: 'Yield Over Time',   icon: '📈' },
    { id: 'yoy',     label: 'Year-over-Year',     icon: '📆' },
    { id: 'variety', label: 'By Variety',         icon: '🌿' },
    { id: 'crop',    label: 'Per Acre',          icon: '🌾' },
    { id: 'weather', label: 'Weather vs Yield',   icon: '🌤' },
    { id: 'expense', label: 'Expense Summary',    icon: '💰' },
    { id: 'revenue', label: 'Revenue by Channel', icon: '🏷' },
    { id: 'insights', label: 'Insights', icon: '✨' },
    { id: 'scheduled', label: 'Scheduled', icon: '🗓' },
  ]

  return (
    <main id="main-content" style={{ padding: 32, fontFamily: '"DM Sans", system-ui, sans-serif', background: C.bg, minHeight: '100dvh', color: C.text }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, fontFamily: '"Syne", system-ui', margin: '0 0 4px' }}>Reports</h1>
        <p style={{ color: C.textMuted, fontSize: 13, margin: 0 }}>Yield analytics, year-over-year trends, and financial summaries.</p>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, flexWrap: 'wrap', borderBottom: `1px solid ${C.border}`, paddingBottom: 0 }}>
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: '8px 16px', borderRadius: '8px 8px 0 0', fontSize: 13, fontWeight: 600,
              background: tab === t.id ? 'color-mix(in srgb, var(--color-primary) 9%, transparent)' : 'transparent',
              color: tab === t.id ? C.amber : C.textMuted,
              border: 'none', borderBottom: tab === t.id ? `2px solid ${C.amber}` : '2px solid transparent',
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
              marginBottom: -1,
            }}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {tab === 'yield'   && <YieldTab />}
      {tab === 'yoy'     && <YoYTab />}
      {tab === 'variety' && <VarietyTab />}
      {tab === 'crop'    && <CropTab />}
      {tab === 'weather' && <WeatherTab />}
      {tab === 'expense' && <ExpenseTab />}
      {tab === 'revenue' && <RevenueTab />}
      {tab === 'insights' && <InsightsTab />}
      {tab === 'scheduled' && <ScheduledTab />}
    </main>
  )
}
